import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { IconComponent } from '../icon';
import { UserProfileService, type PartnerAccess } from './user-profile.service';

/**
 * The organization the signed-in user is currently acting for, and the switch between the others.
 *
 * <p>Selecting an organization re-keys every partner-scoped call on the portal, so it sits centred in
 * the app header rather than on one screen — the data it governs is on all of them, and a switch
 * reachable only from the dashboard would mean navigating back to change what you are looking at.
 *
 * <p>An account with a single organization still gets the control, with that organization selected. It
 * is the same affordance everywhere and it names what you are looking at, which is worth more than the
 * row of pixels saved by hiding it.
 *
 * <h2>Alignment with the CMS Design System</h2>
 *
 * <ul>
 *   <li>A native {@code select}, which is what the CMSDS Dropdown renders. It gets keyboard support,
 *       the platform's own touch and screen-reader handling, and type-ahead for free; a custom listbox
 *       would have to reimplement all of it, and badly.</li>
 *   <li>A visible {@code label}, as the system requires of a form control, naming the action rather
 *       than the field: the point is where you will end up, not that a value is being held.</li>
 *   <li>Each option is the organization's name. The partner id is the value submitted and stays on the
 *       screens themselves, where it is needed for support calls; in the list it only made three
 *       similarly shaped ids harder to tell apart.</li>
 * </ul>
 */
@Component({
  selector: 'app-partner-switcher',
  imports: [IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (profile.partners().length) {
      <div class="partner-switcher">
        <app-icon class="partner-switcher__icon" name="organization" [size]="15" />
        <label class="partner-switcher__label" for="partner-switcher-select">Navigate to organization</label>
        <select
          id="partner-switcher-select"
          class="partner-switcher__select"
          [value]="profile.selectedPartnerId()"
          (change)="choose($event)"
        >
          @for (partner of profile.partners(); track partner.partnerId) {
            <option [value]="partner.partnerId">{{ optionLabel(partner) }}</option>
          }
        </select>
      </div>
    }
  `,
})
export class PartnerSwitcher {
  protected readonly profile = inject(UserProfileService);

  /**
   * The organization's name, which is what the reader recognises.
   *
   * <p>The id is the fallback rather than a suffix: an organization whose id the partner table does
   * not know has no name to show, and an empty option could not be chosen or described.
   */
  protected optionLabel(partner: PartnerAccess): string {
    return partner.partnerName ?? partner.partnerId;
  }

  protected choose(event: Event): void {
    this.profile.selectPartner((event.target as HTMLSelectElement).value);
  }
}
